/**
 * Phân quyền hiển thị trang (v0.36, migration 032) — tải bảng page_access 1 lần
 * cho Sidebar + PageGate; trang /permissions gọi reload() sau khi lưu.
 * Lỗi tải (VD chưa áp migration) ⇒ coi như không có giới hạn thêm (dùng vai trò gốc).
 */
import { createContext, useCallback, useContext, useEffect, useState, type ReactNode } from 'react'
import { useLocation } from 'react-router-dom'
import { Lock } from 'lucide-react'
import { useAuth } from '@/contexts/AuthContext'
import { listPageAccess } from '@/lib/api'
import { ALL_PAGES, canSeePage, type PageAccessRow } from '@/lib/navigation'

interface PageAccessValue {
  access: Map<string, PageAccessRow>
  ready: boolean
  reload: () => Promise<void>
}

const PageAccessContext = createContext<PageAccessValue>({ access: new Map(), ready: true, reload: async () => {} })

export function PageAccessProvider({ children }: { children: ReactNode }) {
  const { session } = useAuth()
  const [access, setAccess] = useState<Map<string, PageAccessRow>>(new Map())
  const [ready, setReady] = useState(false)

  const reload = useCallback(async () => {
    try {
      const rows = await listPageAccess()
      setAccess(new Map(rows.map(r => [r.path, r])))
    } catch { /* bảng chưa có/mạng lỗi — giữ mặc định */ }
    setReady(true)
  }, [])

  useEffect(() => { if (session) reload() }, [session, reload])

  return <PageAccessContext.Provider value={{ access, ready, reload }}>{children}</PageAccessContext.Provider>
}

export const usePageAccess = () => useContext(PageAccessContext)

/** Chặn mở thẳng URL của trang đang bị tắt cho tài khoản này. */
export function PageGate({ children }: { children: ReactNode }) {
  const { profile } = useAuth()
  const { access, ready } = usePageAccess()
  const { pathname } = useLocation()
  const page = ALL_PAGES.find(p => p.to === pathname)
  if (!page || !profile) return <>{children}</>
  if (!ready) return <div className="flex h-64 items-center justify-center text-sm text-muted-foreground">Đang tải trang…</div>
  // Vai trò gốc không có quyền → để ProtectedRoute xử lý như cũ
  if (!page.roles.includes(profile.user.role)) return <>{children}</>
  if (canSeePage(page, profile.user.role, profile.user.id, access, profile.department?.code ?? null)) return <>{children}</>
  return (
    <div className="flex h-64 flex-col items-center justify-center gap-2 text-center text-muted-foreground">
      <Lock className="h-8 w-8" />
      <div className="font-medium text-foreground">Trang "{page.label}" đang tạm khoá</div>
      <div className="text-sm">Chức năng đang được hoàn thiện, sẽ mở khi sẵn sàng.</div>
    </div>
  )
}
